import { AmazonConnectApp } from "@amazon-connect/app";
import type { AppCreateEvent, AppDestroyEvent } from "@amazon-connect/app";
import type { AmazonConnectProvider } from "@amazon-connect/core";
import { createLogger } from "./logger";
import { LiveGateway, MockGateway, type SdkGateway } from "./gateway";
import type { EventLog } from "./eventLog";

const log = createLogger("connection");

/** Where the app lifecycle handshake currently stands. */
export type ConnectionStatus =
  | "connecting"
  | "connected"
  | "timeout"
  | "error"
  | "destroyed"
  | "mock";

/**
 * The fields of `AppContext` (`@amazon-connect/app` 1.0.14) this kit reads or records.
 * `instanceId` is the current field; `appInstanceId` is `@deprecated` in favour of it but is
 * still read as a fallback. `config`, `scope`, `parameters`, `launchedBy`, and `region` are
 * carried through untouched so the full handshake context is available for diagnostics —
 * `launchedBy` and `scope` say why the app opened and which contact it is bound to.
 */
export type AppLikeContext = {
  readonly instanceId?: string;
  readonly appInstanceId?: string;
  readonly config?: unknown;
  readonly scope?: unknown;
  readonly parameters?: unknown;
  readonly launchedBy?: unknown;
  readonly region?: unknown;
};

/**
 * Test-injectable view of the two lifecycle callbacks `AmazonConnectApp.init` takes. Decoupled
 * from the real `AppCreateHandler`/`AppDestroyHandler` types (which return `Promise<void>`) so
 * unit tests can fire them synchronously.
 */
type InitHandlers = {
  onCreate: (event: { context: AppLikeContext }) => void;
  onDestroy: (event: { context?: AppLikeContext }) => void;
};

/** Inputs to {@link connect}: mock-vs-live, where to log lifecycle entries, and test seams. */
export type ConnectOptions = {
  mock: boolean;
  log: EventLog;
  /** Default 5000ms. The workspace handshake is fast; a longer wait just delays the diagnosis. */
  timeoutMs?: number;
  /** Injected for testability; defaults to the real AmazonConnectApp.init. */
  init?: (handlers: InitHandlers) => { provider: AmazonConnectProvider };
};

/** The live handle returned by {@link connect}: current state plus teardown. */
export type ConnectionHandle = {
  status(): ConnectionStatus;
  gateway(): SdkGateway | undefined;
  /** `instanceId`, falling back to the deprecated `appInstanceId`, once connected. */
  appInstanceId(): string | undefined;
  /** The full app context recorded on `onCreate`, once connected. */
  context(): AppLikeContext | undefined;
  onChange(cb: (status: ConnectionStatus) => void): () => void;
  destroy(): void;
};

/**
 * Copies handshake fields only, dropping SDK provider/proxy circulars from the timeline.
 * Missing or exploding context must not throw: that used to leave status stuck on
 * `connecting` after the timeout timer had already been cleared.
 */
function slimContext(ctx: AppLikeContext | undefined | null): AppLikeContext {
  if (!ctx || typeof ctx !== "object") return {};
  try {
    return {
      instanceId: ctx.instanceId,
      appInstanceId: ctx.appInstanceId,
      config: ctx.config,
      scope: ctx.scope,
      parameters: ctx.parameters,
      launchedBy: ctx.launchedBy,
      region: ctx.region,
    };
  } catch (e) {
    log.warn("slimContext failed; recording empty context", e);
    return {};
  }
}

const DEFAULT_TIMEOUT_MS = 20000;
const TERMINAL_STATUSES: readonly ConnectionStatus[] = [
  "timeout",
  "error",
  "destroyed",
];

/** Completes the handshake. Failures become `error`; they must never leave status on `connecting`. */
function handleCreate(
  event: { context: AppLikeContext },
  provider: AmazonConnectProvider,
  state: ConnectionState,
  transition: (next: ConnectionStatus, payload?: unknown) => void,
  isTerminal: () => boolean,
): void {
  if (isTerminal()) {
    log.warn("onCreate fired after terminal state; ignoring", {
      status: state.status,
    });
    return;
  }
  try {
    const ctx = slimContext(event.context);
    if (state.timer) clearTimeout(state.timer);
    state.context = ctx;
    state.gateway = new LiveGateway(provider);
    log.info("handshake connected", {
      instanceId: ctx.instanceId ?? ctx.appInstanceId,
      scope: ctx.scope,
      launchedBy: ctx.launchedBy,
      region: ctx.region,
    });
    transition("connected", ctx);
  } catch (e) {
    const err = e instanceof Error ? e : new Error(String(e));
    log.error("onCreate failed", err);
    if (state.timer) clearTimeout(state.timer);
    transition("error", {
      message: err.message,
      name: err.name,
      stack: err.stack,
    });
  }
}

/** Wraps the real `AmazonConnectApp.init`, adapting its async handler shape to `InitHandlers`. */
function realInit(handlers: InitHandlers): { provider: AmazonConnectProvider } {
  const { provider } = AmazonConnectApp.init({
    onCreate: async (event: AppCreateEvent) => handlers.onCreate(event),
    onDestroy: async (event: AppDestroyEvent) => handlers.onDestroy(event),
  });
  return { provider };
}

/** The mutable connection state shared between `connect()` and its live-init helper. */
type ConnectionState = {
  status: ConnectionStatus;
  gateway: SdkGateway | undefined;
  context: AppLikeContext | undefined;
  timer: ReturnType<typeof setTimeout> | undefined;
};

/**
 * Starts the real `AmazonConnectApp.init` handshake: arms the connect timeout and wires
 * `onCreate`/`onDestroy` to update the shared connection `state` via `transition`.
 *
 * A late `onCreate` arriving after the handshake has already timed out, or after `destroy()`
 * was called, is ignored: it must not construct a gateway or move status away from its
 * terminal value (amendment A11).
 *
 * `init` itself can throw — the real SDK guards re-initialisation with a process-level flag —
 * so it is called inside a try/catch and BEFORE the timeout is armed. A failed init is a
 * terminal `error` status carrying the real cause, not a propagated throw and not a timer left
 * running to append a misleading `timeout` entry minutes later.
 */
function startLiveConnection(
  opts: ConnectOptions,
  state: ConnectionState,
  transition: (next: ConnectionStatus, payload?: unknown) => void,
): void {
  const isTerminal = (): boolean => TERMINAL_STATUSES.includes(state.status);
  const initFn = opts.init ?? realInit;

  try {
    const { provider } = initFn({
      onCreate: (event) => {
        handleCreate(event, provider, state, transition, isTerminal);
      },
      onDestroy: (event) => {
        if (state.timer) clearTimeout(state.timer);
        log.info("onDestroy", { instanceId: event.context?.instanceId });
        transition("destroyed", slimContext(event.context));
      },
    });
    log.info("AmazonConnectApp.init returned; waiting for onCreate", {
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    });
  } catch (e) {
    const err = e instanceof Error ? e : new Error(String(e));
    log.error("app init failed", err);
    transition("error", { message: err.message, name: err.name });
    return;
  }

  // Armed only once init has returned, and only if the handshake is still outstanding: a
  // synchronous onCreate may already have completed it.
  if (state.status !== "connecting") return;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  state.timer = setTimeout(() => {
    if (state.status === "connecting") {
      log.warn("handshake timed out; not inside an agent workspace iframe", {
        timeoutMs,
      });
      transition("timeout");
    }
  }, timeoutMs);
}

/**
 * Initialises the app against the agent workspace, or a mock gateway when requested.
 * A failed handshake is a first-class status, not a console error: loading the app outside
 * the workspace always fails, and that needs to be legible in the UI.
 */
export function connect(opts: ConnectOptions): ConnectionHandle {
  const subscribers = new Set<(s: ConnectionStatus) => void>();
  const state: ConnectionState = {
    status: opts.mock ? "mock" : "connecting",
    gateway: opts.mock ? new MockGateway() : undefined,
    context: undefined,
    timer: undefined,
  };

  const transition = (next: ConnectionStatus, payload?: unknown): void => {
    if (next === state.status) return;
    state.status = next;
    opts.log.append({
      kind: "lifecycle",
      moduleId: "app",
      name: next,
      payload,
    });
    for (const cb of subscribers) {
      try {
        cb(next);
      } catch (e) {
        log.error("status subscriber threw", e);
      }
    }
  };

  if (opts.mock) {
    log.info("mock mode; skipping workspace handshake");
    opts.log.append({ kind: "lifecycle", moduleId: "app", name: "mock" });
  } else {
    log.info("starting live handshake", {
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    });
    startLiveConnection(opts, state, transition);
  }

  return {
    status: () => state.status,
    gateway: () => state.gateway,
    appInstanceId: () =>
      state.context?.instanceId ?? state.context?.appInstanceId,
    context: () => state.context,
    onChange: (cb) => {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
    destroy: () => {
      if (state.timer) clearTimeout(state.timer);
      transition("destroyed");
    },
  };
}
